import { LearnedRecommendation, Recommendation } from '@coach-bot/data-access/shared/interfaces';
import { AbstractApiRequestService } from '@coach-bot/data-access/shared/services/abstract/abstract-api-request.service';
import { AbstractStudentStore } from '@coach-bot/data-access/shared/stores/abstract/abstract-student.store';
import { ApiRequestType } from '@shared/extension/enums/api-request-type.enum';
import { FetchRequestData } from '@shared/extension/interfaces/api-request.interface';
import { AbstractService } from '@shared/ui/services/abstract.service';
import {
  IXL_PRACTICE_SUMMARY_REQUEST_PATH,
  IXL_SAVE_QUIZ_RESPONSE_REQUEST_PATH,
  ONE_DAY_MS,
  ONE_HOUR_MS,
  ONE_SECOND_MS,
  ONE_WEEK_MS,
  getLearningApp,
  sleep,
} from '@shared/utils';
import { BehaviorSubject, Subscription, interval, takeUntil, timer } from 'rxjs';
import {
  DEFAULT_WATCH_RECOMMENDATIONS_INTERVAL_MS,
  DEFAULT_WATCH_STUDENT_BALANCE_INTERVAL_MS,
  LEARNING_COMPLETED_WATCH_RECOMMENDATIONS_INTERVAL_MS,
  LEARNING_COMPLETED_WATCH_RECOMMENDATIONS_TIMEOUT_MS,
  LEARNING_COMPLETED_WATCH_STUDENT_BALANCE_INTERVAL_MS,
  LEARNING_COMPLETED_WATCH_STUDENT_BALANCE_TIMEOUT_MS,
} from '../constants';
import { StudentState } from '../states';
import { getIXLQuizId } from '../utils';

interface IXLLearningSummary {
  skillId: number;
  skillMastered: boolean;
}

const CHECK_LEARNED_RECOMMENDATIONS_INTERVAL_MS = ONE_HOUR_MS;
const IXL_APP_NAME = 'IXL';
const LEARNED_RECOMMENDATION_EXPIRATION_TIME_MS = ONE_WEEK_MS;
const LEARNING_ACTIVITY_FIRST_REMINDER_INTERVAL_MS = ONE_DAY_MS;
const LEARNING_ACTIVITY_SECOND_REMINDER_INTERVAL_MS = ONE_DAY_MS * 5;
const LOAD_DATA_DELAY_MS = ONE_SECOND_MS * 5;

export class LearningService extends AbstractService {
  private checkLearnedRecommendationsSubscription: Subscription | null = null;

  constructor(
    private apiRequestService: AbstractApiRequestService,
    private lastSignInTime$: BehaviorSubject<number | null>,
    private studentState: StudentState,
    private studentStore: AbstractStudentStore
  ) {
    super();
  }

  startCheckingLearnedRecommendations(): void {
    if (this.checkLearnedRecommendationsSubscription) {
      return;
    }
    // We don't want to store learned recommendations forever, so we check them periodically and remove old ones
    this.checkLearnedRecommendationsSubscription = interval(CHECK_LEARNED_RECOMMENDATIONS_INTERVAL_MS)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.checkLearnedRecommendations();
      });
  }

  stopCheckingLearnedRecommendations(): void {
    if (this.checkLearnedRecommendationsSubscription) {
      this.checkLearnedRecommendationsSubscription.unsubscribe();
      this.checkLearnedRecommendationsSubscription = null;
    }
  }

  /**
   * Handles the completion of a learning activity and sets intervals to watch for reward updates.
   * @param {string} summaryUrl - A string representing the URL of the summary of mastered skill.
   * @returns Promise<void>.
   */
  async handleLearningCompleted(learnedRecommendation: LearnedRecommendation): Promise<void> {
    const learnedRecommendations: LearnedRecommendation[] | null = this.studentStore.learnedRecommendations$.value;
    const learnedBefore: boolean = learnedRecommendations?.some(
      (item: LearnedRecommendation) => learnedRecommendation.resourceUrl === item.resourceUrl
    );
    if (learnedBefore) {
      this.studentState.loadRecommendations();
      return;
    }
    await this.saveLearnedRecommendation(learnedRecommendation);

    if (learnedRecommendation.detailsRequestUrl.includes(IXL_PRACTICE_SUMMARY_REQUEST_PATH)) {
      await this.handleSkillMastered(learnedRecommendation);
    } else if (learnedRecommendation.detailsRequestUrl.includes(IXL_SAVE_QUIZ_RESPONSE_REQUEST_PATH)) {
      await this.handleQuizCompleted(learnedRecommendation);
    }

    const recommendations: Recommendation[] | null = this.studentStore.recommendations$.value;
    if (recommendations) {
      const filteredRecommendations: Recommendation[] = this.studentState.filterRecommendations(
        recommendations,
        learnedRecommendations
      );
      if (JSON.stringify(recommendations) !== JSON.stringify(filteredRecommendations)) {
        await this.studentStore.setRecommendations(filteredRecommendations.length > 0 ? filteredRecommendations : null);
      }
    }
  }

  /**
   * Checks if any learned recommendations have expired and updates the list of learned
   * recommendations if necessary.
   * @returns Promise<void>.
   */
  async checkLearnedRecommendations(): Promise<void> {
    const oldLearnedRecommendations: LearnedRecommendation[] | null = this.studentStore.learnedRecommendations$.value;
    if (!oldLearnedRecommendations) {
      return;
    }
    const now: number = Date.now();
    const newLearnedRecommendations: LearnedRecommendation[] = oldLearnedRecommendations.filter(
      (recommendation: LearnedRecommendation) =>
        now - recommendation.learnedTime < LEARNED_RECOMMENDATION_EXPIRATION_TIME_MS
    );
    if (JSON.stringify(oldLearnedRecommendations) !== JSON.stringify(newLearnedRecommendations)) {
      await this.studentStore.setLearnedRecommendations(newLearnedRecommendations);
    }
  }

  async checkLastLearningActivity(): Promise<boolean> {
    const lastLearningActivityTime: number =
      this.studentStore.lastLearningActivityTime$.value || this.lastSignInTime$.value || Date.now();
    let learningReminderDisplayTimes: number[] | null = this.studentStore.learningReminderDisplayTimes$.value;
    let showLearningReminder: boolean = false;
    if (
      learningReminderDisplayTimes?.length > 0 &&
      lastLearningActivityTime >= learningReminderDisplayTimes[learningReminderDisplayTimes.length - 1]
    ) {
      await this.studentStore.setLearningReminderDisplayTimes(null);
    } else {
      const now: number = Date.now();
      if (!learningReminderDisplayTimes || learningReminderDisplayTimes.length === 0) {
        if (now - lastLearningActivityTime >= LEARNING_ACTIVITY_FIRST_REMINDER_INTERVAL_MS) {
          learningReminderDisplayTimes = [now];
          showLearningReminder = true;
        }
      }
      if (learningReminderDisplayTimes?.length === 1) {
        if (now - lastLearningActivityTime >= LEARNING_ACTIVITY_SECOND_REMINDER_INTERVAL_MS) {
          learningReminderDisplayTimes = [...learningReminderDisplayTimes, now];
          showLearningReminder = true;
        }
      }
      await this.studentStore.setLearningReminderDisplayTimes(learningReminderDisplayTimes || null);
    }
    return showLearningReminder;
  }

  private async handleSkillMastered(learnedRecommendation: LearnedRecommendation): Promise<void> {
    // By default we set completed to true, but if we double check it by loading summary data
    let completed: boolean = true;
    try {
      const summary: unknown = this.apiRequestService.getApiRequestData({
        type: ApiRequestType.Fetch,
        data: {
          url: learnedRecommendation.detailsRequestUrl,
        } as FetchRequestData,
      });
      const learningApp: string = getLearningApp(learnedRecommendation.detailsRequestUrl);
      if (learningApp === IXL_APP_NAME) {
        const summaryData = summary as IXLLearningSummary;
        if (typeof summaryData.skillMastered === 'boolean') {
          completed = summaryData.skillMastered;
        }
        if (typeof summaryData.skillId === 'number') {
          learnedRecommendation.resourceId = summaryData.skillId.toString();
          await this.saveLearnedRecommendation(learnedRecommendation);
        }
      }
    } catch (error: unknown) {
      console.error(`Error while getting learning completed summary: ${error}`);
    }
    if (completed) {
      await this.startWaitingForReward(learnedRecommendation);
    }
  }

  private async handleQuizCompleted(learnedRecommendation: LearnedRecommendation): Promise<void> {
    const placementQuizId: string | null = getIXLQuizId(learnedRecommendation.resourceUrl);
    if (placementQuizId) {
      learnedRecommendation.resourceId = placementQuizId;
      await this.saveLearnedRecommendation(learnedRecommendation);
    }
    await this.startWaitingForReward(learnedRecommendation);
  }

  private async startWaitingForReward(learnedRecommendation: LearnedRecommendation): Promise<void> {
    const recommendations: Recommendation[] | null = this.studentStore.recommendations$.value;
    const recommendation: Recommendation | undefined = recommendations?.find(
      (item: Recommendation) =>
        item.url === learnedRecommendation.resourceUrl || item.id === learnedRecommendation.resourceId
    );
    await this.studentStore.setWaitingForRewardRecommendation(recommendation || null);

    // When student mastered skill or completed quiz, we expect reward to be added to student balance and
    // recommendations to be updated. We start checking data more often. At the moment we don't have any events
    // from backend and we use interval to load data. Later need to change this logic.
    this.studentStore.setWatchRecommendationsIntervalMs(LEARNING_COMPLETED_WATCH_RECOMMENDATIONS_INTERVAL_MS);
    this.studentStore.setWatchStudentBalanceIntervalMs(LEARNING_COMPLETED_WATCH_STUDENT_BALANCE_INTERVAL_MS);
    timer(LEARNING_COMPLETED_WATCH_RECOMMENDATIONS_TIMEOUT_MS)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.studentStore.setWatchRecommendationsIntervalMs(DEFAULT_WATCH_RECOMMENDATIONS_INTERVAL_MS);
      });
    timer(LEARNING_COMPLETED_WATCH_STUDENT_BALANCE_TIMEOUT_MS)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async () => {
        this.studentStore.setWatchStudentBalanceIntervalMs(DEFAULT_WATCH_STUDENT_BALANCE_INTERVAL_MS);
        const waitingForRewardRecommendation: Recommendation | null =
          this.studentStore.waitingForRewardRecommendation$.value;
        if (waitingForRewardRecommendation) {
          await this.studentState.loadRecommendations();
          await this.studentStore.setWaitingForRewardRecommendation(null);
        }
      });
    // Wait some time, probably on backend reward will be processed during that time
    // This is temp code until we have events from backend
    sleep(LOAD_DATA_DELAY_MS).then(() => {
      this.studentState.loadStudentBalance();
    });
  }

  private async saveLearnedRecommendation(learnedRecommendation: LearnedRecommendation): Promise<void> {
    let learnedRecommendations: LearnedRecommendation[] | null = this.studentStore.learnedRecommendations$.value;
    learnedRecommendations = learnedRecommendations || [];
    const existingItem = learnedRecommendations.find(
      (item: LearnedRecommendation) => learnedRecommendation.resourceUrl === item.resourceUrl
    );
    if (existingItem) {
      Object.assign(existingItem, learnedRecommendation);
    } else {
      learnedRecommendations.push(learnedRecommendation);
    }
    await this.studentStore.setLearnedRecommendations([...learnedRecommendations]);
  }
}
